// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { IResourceRepository } from './interfaces';
import {
  normalizeResourceOwnerFields,
  RESOURCE_OWNER,
} from '@shared/models/resource-doc';

// Resource entity interfaces (matching actual database structure)
export interface CreateResourceData {
  title: string;
  description?: string;
  type: 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO' | 'TEXT' | 'INTERACTIVE';
  url: string;
  thumbnailUrl?: string;
  instructorId: string;
  sellerId?: string;
  categoryId?: string;
  danceStyle?: string;
  difficultyLevel?: 'Beginner' | 'Intermediate' | 'Advanced';
  ageRange?: string;
  price?: number;
  isFeatured?: boolean;
  isActive?: boolean;
  isApproved?: boolean;
  status?: 'pending' | 'approved' | 'rejected';
}

export interface UpdateResourceData {
  title?: string;
  description?: string;
  detailedDescription?: string;
  resourceUrl?: string;
  thumbnailUrl?: string;
  coverPhotoUrl?: string;
  fileSize?: number;
  durationMinutes?: number;
  courseId?: string;
  moduleId?: string;
  lessonId?: string;
  isPremium?: boolean;
  price?: number;
  isPublished?: boolean;
  isFeatured?: boolean;
  status?: string;
  type?: string;
  resourceType?: string;
  tags?: string[];
  categoryIds?: string[];
  /** Seller panel uses title-case; curriculum docs may use lowercase. */
  difficultyLevel?: 'beginner' | 'intermediate' | 'advanced' | 'Beginner' | 'Intermediate' | 'Advanced';
  danceStyle?: string;
  ageRange?: string;
  files?: Array<{ url: string; type: string; name: string }>;
  links?: string[];
  fileUrl?: string;
  fileType?: string;
  fullVideoUrl?: string;
  previewVideoUrl?: string;
  language?: string;
  prerequisites?: string[];
  learningObjectives?: string[];
  updatedAt?: Timestamp;
}

export interface ResourceFilter {
  instructorId?: string;
  courseId?: string;
  moduleId?: string;
  lessonId?: string;
  contentType?: 'video' | 'document' | 'image' | 'audio' | 'text' | 'interactive';
  isPremium?: boolean;
  isPublished?: boolean;
  isFeatured?: boolean;
  difficultyLevel?: 'beginner' | 'intermediate' | 'advanced';
  categoryId?: string;
  tag?: string;
  language?: string;
}

export interface GetResourceData {
  id: string;
  title: string;
  description?: string;
  type: 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO' | 'TEXT' | 'INTERACTIVE';
  url: string;
  thumbnailUrl?: string;
  instructorId?: string;
  sellerId?: string;
  categoryId?: string;
  danceStyle?: string;
  difficultyLevel?: 'Beginner' | 'Intermediate' | 'Advanced';
  ageRange?: string;
  price?: number;
  isFeatured?: boolean;
  isActive?: boolean;
  isApproved?: boolean;
  status?: 'pending' | 'approved' | 'rejected';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  downloadCount?: number;
  viewCount?: number;
}

export interface ResourceViewData {
  id: string;
  userId: string;
  resourceId: string;
  viewedAt: Timestamp;
  timeSpentSeconds?: number;
  completionPercentage?: number;
  deviceType?: string;
  ipAddress?: string;
}

export interface ResourceDownloadData {
  id: string;
  userId: string;
  resourceId: string;
  downloadedAt: Timestamp;
  downloadCount: number;
}

// Resource Category interfaces
export interface CreateResourceCategoryData {
  name: string;
  description?: string;
  imageUrl?: string;
}

export interface GetResourceCategoryData {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  resourceCount?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface UpdateResourceCategoryData {
  name?: string;
  description?: string;
  imageUrl?: string;
}

/** Withdrawn by its seller (deleteOwnResource): kept for buyers, out of every listing. */
const isListed = (r: GetResourceData) => (r as { withdrawn?: boolean }).withdrawn !== true;

function mapSnapToGetResourceData(docSnap: DocumentSnapshot): GetResourceData {
  const raw = (docSnap.data() || {}) as Record<string, unknown>;
  const owners = normalizeResourceOwnerFields(raw);
  return {
    id: docSnap.id,
    ...raw,
    ...owners,
  } as GetResourceData;
}

export class ResourceRepository implements IResourceRepository {
  constructor() {
    console.log('[ResourceRepository] Constructor - Using client-side Firebase DB instance:', !!db);
  }

  private collectionName = 'resources';
  private categoriesCollectionName = 'resourceCategories';
  private viewsCollectionName = 'resourceViews';
  private downloadsCollectionName = 'resourceDownloads';

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  // Create resource
  async createResource(resourceData: CreateResourceData): Promise<GetResourceData> {
    console.log('[ResourceRepository.createResource] START - ResourceData:', resourceData);
    try {
      console.log('[ResourceRepository.createResource] Creating resource with instructorId:', resourceData.instructorId);

      const { instructorId, sellerId, ...rest } = resourceData;
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...rest,
        [RESOURCE_OWNER.instructorId]: instructorId,
        ...(sellerId ? { [RESOURCE_OWNER.sellerId]: sellerId } : {}),
        // Set defaults for optional fields
        isFeatured: resourceData.isFeatured ?? false,
        isActive: resourceData.isActive ?? true,
        isApproved: resourceData.isApproved ?? false,
        status: resourceData.status ?? 'pending',
        // Use correct timestamp field names to match database
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'RESOURCE_CREATE_MISSING_DOCUMENT',
          devMessage: 'Resource document was missing after create',
        });
      }

      const result = mapSnapToGetResourceData(docSnap);

      console.log('[ResourceRepository.createResource] SUCCESS - Created resource:', result.id, 'for instructor:', result.instructorId);
      return result;
    } catch (error) {
      console.error('[ResourceRepository.createResource] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCE_CREATE_FAILED', 'Failed to create resource');
    }
  }

  // Get resource by ID
  async getResourceById(resourceId: string): Promise<GetResourceData | null> {
    try {
      const docRef = doc(db, this.collectionName, resourceId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return mapSnapToGetResourceData(docSnap);
    } catch (error) {
      console.error('Error getting resource by ID:', error);
      throw this.toRepositoryError(error, 'RESOURCE_FETCH_FAILED', `Failed to get resource ${resourceId}`);
    }
  }

  async listCourseCurriculumResourcesByCourseId(
    courseId: string,
  ): Promise<Array<{ id: string } & Record<string, unknown>>> {
    const courseResourcesCollection = 'course-resources';
    try {
      const q = query(
        collection(db, courseResourcesCollection),
        where('courseId', '==', courseId),
        orderBy('createdAt', 'desc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error listing course curriculum resources:', error);
      throw this.toRepositoryError(
        error,
        'COURSE_RESOURCES_FETCH_FAILED',
        `Failed to list course-resources for course ${courseId}`,
      );
    }
  }

  // Get resources with filters
  async getResources(filter: ResourceFilter = {}, limitCount?: number): Promise<GetResourceData[]> {
    try {
      const col = collection(db, this.collectionName);

      if (filter.instructorId) {
        const uid = filter.instructorId;
        const rest = { ...filter };
        delete (rest as { instructorId?: string }).instructorId;

        const constraints: ReturnType<typeof where>[] = [];
        constraints.push(where(RESOURCE_OWNER.instructorId, '==', uid));
        if (rest.courseId) constraints.push(where('courseId', '==', rest.courseId));
        if (rest.moduleId) constraints.push(where('moduleId', '==', rest.moduleId));
        if (rest.lessonId) constraints.push(where('lessonId', '==', rest.lessonId));
        if (rest.contentType) constraints.push(where('contentType', '==', rest.contentType));
        if (rest.isPremium !== undefined) constraints.push(where('isPremium', '==', rest.isPremium));
        if (rest.isPublished !== undefined) constraints.push(where('isPublished', '==', rest.isPublished));
        if (rest.isFeatured !== undefined) constraints.push(where('isFeatured', '==', rest.isFeatured));
        if (rest.difficultyLevel) constraints.push(where('difficultyLevel', '==', rest.difficultyLevel));
        if (rest.categoryId) constraints.push(where('categoryIds', 'array-contains', rest.categoryId));
        if (rest.tag) constraints.push(where('tags', 'array-contains', rest.tag));
        if (rest.language) constraints.push(where('language', '==', rest.language));
        if (limitCount) constraints.push(limit(limitCount));

        const q = query(col, ...constraints);
        const snap = await getDocs(q);
        const merged: GetResourceData[] = [];
        snap.forEach((d) => merged.push(mapSnapToGetResourceData(d)));
        return merged.filter(isListed);
      }

      let q = col;

      // Build query constraints array
      const constraints = [];

      // Add filters
      if (filter.courseId) {
        constraints.push(where('courseId', '==', filter.courseId));
      }
      if (filter.moduleId) {
        constraints.push(where('moduleId', '==', filter.moduleId));
      }
      if (filter.lessonId) {
        constraints.push(where('lessonId', '==', filter.lessonId));
      }
      if (filter.contentType) {
        constraints.push(where('contentType', '==', filter.contentType));
      }
      if (filter.isPremium !== undefined) {
        constraints.push(where('isPremium', '==', filter.isPremium));
      }
      if (filter.isPublished !== undefined) {
        constraints.push(where('isPublished', '==', filter.isPublished));
      }
      if (filter.isFeatured !== undefined) {
        constraints.push(where('isFeatured', '==', filter.isFeatured));
      }
      if (filter.difficultyLevel) {
        constraints.push(where('difficultyLevel', '==', filter.difficultyLevel));
      }
      if (filter.categoryId) {
        constraints.push(where('categoryIds', 'array-contains', filter.categoryId));
      }
      if (filter.tag) {
        constraints.push(where('tags', 'array-contains', filter.tag));
      }
      if (filter.language) {
        constraints.push(where('language', '==', filter.language));
      }

      // Add limit if specified
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      // Build the final query
      if (constraints.length > 0) {
        q = query(q, ...constraints);
        console.log('[ResourceRepository.getResources] Built query with', constraints.length, 'constraints');
      } else {
        console.log('[ResourceRepository.getResources] No constraints, using simple collection query');
      }

      const querySnapshot = await getDocs(q);
      const resources: GetResourceData[] = [];

      querySnapshot.forEach((d) => {
        resources.push(mapSnapToGetResourceData(d));
      });

      return resources.filter(isListed);
    } catch (error) {
      console.error('Error getting resources:', error);
      throw this.toRepositoryError(error, 'RESOURCES_FETCH_FAILED', 'Failed to get resources');
    }
  }

  // Get all resources (admin access)
  async getAllResources(limitCount?: number): Promise<GetResourceData[]> {
    console.log('[ResourceRepository.getAllResources] START - LimitCount:', limitCount);
    try {
      console.log('[ResourceRepository.getAllResources] Retrieving all resources (admin access)');
      const result = await this.getResources({}, limitCount);
      console.log('[ResourceRepository.getAllResources] SUCCESS - Retrieved', result.length, 'resources');
      return result;
    } catch (error) {
      console.error('[ResourceRepository.getAllResources] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCES_ALL_FETCH_FAILED', 'Failed to get all resources');
    }
  }

  // Get resources by instructor ID (for control panel)
  async getResourcesByInstructor(instructorId: string, limitCount?: number): Promise<GetResourceData[]> {
    console.log('[ResourceRepository.getResourcesByInstructor] START - InstructorId:', instructorId, 'LimitCount:', limitCount);
    try {
      console.log('[ResourceRepository.getResourcesByInstructor] Retrieving resources for instructor:', instructorId);
      const result = await this.getResources({ instructorId }, limitCount);
      console.log('[ResourceRepository.getResourcesByInstructor] SUCCESS - Retrieved', result.length, 'resources for instructor');
      return result;
    } catch (error) {
      console.error('[ResourceRepository.getResourcesByInstructor] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_INSTRUCTOR_FETCH_FAILED', `Failed to get resources for instructor ${instructorId}`);
    }
  }

  // Get resources by owner (`instructorId` and/or `sellerId`, camelCase only)
  async getResourcesByOwner(ownerId: string, limitCount?: number): Promise<GetResourceData[]> {
    console.log('[ResourceRepository.getResourcesByOwner] START - OwnerId:', ownerId, 'LimitCount:', limitCount);
    try {
      const col = collection(db, this.collectionName);
      const mk = (field: string) => {
        let q = query(col, where(field, '==', ownerId));
        if (limitCount) q = query(q, limit(limitCount));
        return getDocs(q);
      };

      const [snapInstructor, snapSeller] = await Promise.all([
        mk(RESOURCE_OWNER.instructorId),
        mk(RESOURCE_OWNER.sellerId),
      ]);

      const resourcesMap = new Map<string, GetResourceData>();

      [snapInstructor, snapSeller].forEach((snap) => {
        snap.forEach((d) => {
          if (!resourcesMap.has(d.id)) {
            resourcesMap.set(d.id, mapSnapToGetResourceData(d));
          }
        });
      });

      const result = Array.from(resourcesMap.values()).filter(isListed);
      console.log('[ResourceRepository.getResourcesByOwner] SUCCESS - Retrieved', result.length, 'resources for owner');
      return result;
    } catch (error) {
      console.error('[ResourceRepository.getResourcesByOwner] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_OWNER_FETCH_FAILED', `Failed to get resources for owner ${ownerId}`);
    }
  }

  // Update resource
  async updateResource(resourceId: string, updateData: UpdateResourceData): Promise<GetResourceData> {
    try {
      const docRef = doc(db, this.collectionName, resourceId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'RESOURCE_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Resource ${resourceId} not found after update`,
          userMessage: 'Resource not found.',
          level: 'warning',
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetResourceData;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw this.toRepositoryError(error, 'RESOURCE_UPDATE_FAILED', `Failed to update resource ${resourceId}`);
    }
  }

  // Delete resource: through the server, which checks ownership and keeps a
  // resource someone has bought (withdrawn from sale instead of deleted).
  async deleteResource(resourceId: string): Promise<'deleted' | 'withdrawn'> {
    try {
      const remove = httpsCallable<{ resourceId: string }, { outcome: 'deleted' | 'withdrawn' }>(
        functions,
        'deleteOwnResource'
      );
      return (await remove({ resourceId })).data.outcome;
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw this.toRepositoryError(error, 'RESOURCE_DELETE_FAILED', `Failed to delete resource ${resourceId}`);
    }
  }

  // Get resource by title
  async getResourceByTitle(title: string): Promise<GetResourceData | null> {
    try {
      const q = query(collection(db, this.collectionName), where('title', '==', title));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as GetResourceData;
    } catch (error) {
      console.error('Error getting resource by title:', error);
      throw this.toRepositoryError(error, 'RESOURCE_BY_TITLE_FETCH_FAILED', `Failed to get resource by title ${title}`);
    }
  }

  // Get resources by content type
  async getResourcesByContentType(contentType: 'video' | 'document' | 'image' | 'audio' | 'text' | 'interactive', limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { contentType: contentType };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting resources by content type:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_CONTENT_TYPE_FETCH_FAILED', `Failed to get resources by content type ${contentType}`);
    }
  }

  // Get resources by difficulty level
  async getResourcesByDifficultyLevel(difficultyLevel: 'beginner' | 'intermediate' | 'advanced', limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { difficultyLevel: difficultyLevel };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting resources by difficulty level:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_DIFFICULTY_FETCH_FAILED', `Failed to get resources by difficulty ${difficultyLevel}`);
    }
  }

  // Get resources by language
  async getResourcesByLanguage(language: string, limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { language };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting resources by language:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_LANGUAGE_FETCH_FAILED', `Failed to get resources by language ${language}`);
    }
  }

  // Get resources by category
  async getResourcesByCategory(categoryId: string, limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { categoryId: categoryId };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting resources by category:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_CATEGORY_FETCH_FAILED', `Failed to get resources by category ${categoryId}`);
    }
  }

  // Get resources by tag
  async getResourcesByTag(tagId: string, limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { tag: tagId };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting resources by tag:', error);
      throw this.toRepositoryError(error, 'RESOURCES_BY_TAG_FETCH_FAILED', `Failed to get resources by tag ${tagId}`);
    }
  }

  // Get all resources (admin only) or owner's resources
  async getResourcesForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetResourceData[]> {
    try {
      if (isAdmin) {
        // Admin sees all resources
        return this.getResources({}, limitCount);
      } else {
        // Regular user sees only their own resources
        return this.getResourcesByOwner(userId, limitCount);
      }
    } catch (error) {
      console.error('Error getting resources for user:', error);
      throw this.toRepositoryError(error, 'RESOURCES_FOR_USER_FETCH_FAILED', `Failed to get resources for user ${userId}`);
    }
  }

  // Upload resource thumbnail
  async uploadResourceThumbnail(resourceId: string, file: File): Promise<string> {
    try {
      console.log('Uploading resource thumbnail for resource:', resourceId, 'File:', file.name);

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate a mock URL
      const thumbnailUrl = `https://storage.example.com/resources/${resourceId}/thumbnail_${Date.now()}_${file.name}`;

      // Update resource with new thumbnail URL
      await this.updateResource(resourceId, { thumbnailUrl: thumbnailUrl });

      return thumbnailUrl;
    } catch (error) {
      console.error('Error uploading resource thumbnail:', error);
      throw this.toRepositoryError(error, 'RESOURCE_THUMBNAIL_UPLOAD_FAILED', `Failed to upload thumbnail for resource ${resourceId}`);
    }
  }

  // Upload resource file (video, document, audio, etc.)
  async uploadResourceFile(resourceId: string, file: File): Promise<string> {
    try {
      console.log('Uploading resource file for resource:', resourceId, 'File:', file.name);

      // Get resource to determine content type and upload time
      const resource = await this.getResourceById(resourceId);
      if (!resource) {
        throw new DRXError({
          code: 'RESOURCE_NOT_FOUND',
          devMessage: `Resource ${resourceId} not found`,
          userMessage: 'Resource not found.',
          level: 'warning',
        });
      }

      // Different upload times based on content type
      let uploadDelay = 1000; // Default
      if (resource.contentType === 'video') {
        uploadDelay = 4000; // Videos take longer
      } else if (resource.contentType === 'audio') {
        uploadDelay = 2000; // Audio takes medium time
      }

      await new Promise(resolve => setTimeout(resolve, uploadDelay));

      // Generate a mock URL based on content type
      const fileUrl = `https://storage.example.com/resources/${resourceId}/${resource.contentType}_${Date.now()}_${file.name}`;

      // Update resource with new file URL
      await this.updateResource(resourceId, { resourceUrl: fileUrl });

      return fileUrl;
    } catch (error) {
      console.error('Error uploading resource file:', error);
      throw this.toRepositoryError(error, 'RESOURCE_FILE_UPLOAD_FAILED', `Failed to upload file for resource ${resourceId}`);
    }
  }

  // Upload resource video (specific method for video resources)
  async uploadResourceVideo(resourceId: string, file: File): Promise<string> {
    try {
      // Verify resource is video type
      const resource = await this.getResourceById(resourceId);
      if (!resource) {
        throw new DRXError({
          code: 'RESOURCE_NOT_FOUND',
          devMessage: `Resource ${resourceId} not found`,
          userMessage: 'Resource not found.',
          level: 'warning',
        });
      }

      if (resource.contentType !== 'video') {
        throw new DRXError({
          code: 'RESOURCE_INVALID_VIDEO_TYPE',
          devMessage: `Resource ${resourceId} is not a video type`,
          userMessage: 'This resource is not a video.',
          level: 'warning',
        });
      }

      return this.uploadResourceFile(resourceId, file);
    } catch (error) {
      console.error('Error uploading resource video:', error);
      throw this.toRepositoryError(error, 'RESOURCE_VIDEO_UPLOAD_FAILED', `Failed to upload video for resource ${resourceId}`);
    }
  }

  // Delete resource thumbnail
  async deleteResourceThumbnail(resourceId: string): Promise<void> {
    try {
      const resource = await this.getResourceById(resourceId);
      if (!resource?.thumbnailUrl) {
        return; // No thumbnail to delete
      }

      // In real implementation, delete from cloud storage
      console.log('Deleting resource thumbnail:', resource.thumbnailUrl);

      // Update resource to remove thumbnail URL
      await this.updateResource(resourceId, { thumbnailUrl: null });
    } catch (error) {
      console.error('Error deleting resource thumbnail:', error);
      throw this.toRepositoryError(error, 'RESOURCE_THUMBNAIL_DELETE_FAILED', `Failed to delete thumbnail for resource ${resourceId}`);
    }
  }

  // Delete resource file
  async deleteResourceFile(resourceId: string): Promise<void> {
    try {
      const resource = await this.getResourceById(resourceId);
      if (!resource?.resourceUrl) {
        return; // No file to delete
      }

      // In real implementation, delete from cloud storage
      console.log('Deleting resource file:', resource.resourceUrl);

      // Update resource to remove file URL
      await this.updateResource(resourceId, { resourceUrl: null });
    } catch (error) {
      console.error('Error deleting resource file:', error);
      throw this.toRepositoryError(error, 'RESOURCE_FILE_DELETE_FAILED', `Failed to delete file for resource ${resourceId}`);
    }
  }

  // Get published resources
  async getPublishedResources(limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { isPublished: true };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting published resources:', error);
      throw this.toRepositoryError(error, 'RESOURCES_PUBLISHED_FETCH_FAILED', 'Failed to get published resources');
    }
  }

  // Get free resources
  async getFreeResources(limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { isPremium: false, isPublished: true };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting free resources:', error);
      throw this.toRepositoryError(error, 'RESOURCES_FREE_FETCH_FAILED', 'Failed to get free resources');
    }
  }

  // Get premium resources
  async getPremiumResources(limitCount?: number): Promise<GetResourceData[]> {
    try {
      const filter: ResourceFilter = { isPremium: true, isPublished: true };
      return this.getResources(filter, limitCount);
    } catch (error) {
      console.error('Error getting premium resources:', error);
      throw this.toRepositoryError(error, 'RESOURCES_PREMIUM_FETCH_FAILED', 'Failed to get premium resources');
    }
  }

  // Record resource view
  async recordResourceView(viewData: Omit<ResourceViewData, 'id' | 'viewedAt'>): Promise<ResourceViewData> {
    try {
      const docRef = await addDoc(collection(db, this.viewsCollectionName), {
        ...viewData,
        viewedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'RESOURCE_VIEW_CREATE_MISSING_DOCUMENT',
          devMessage: `Resource view for resource ${viewData.resourceId} missing after create`,
        });
      }

      return {
        id: docRef.id,
        ...docSnap.data(),
      } as ResourceViewData;
    } catch (error) {
      console.error('Error recording resource view:', error);
      throw this.toRepositoryError(error, 'RESOURCE_VIEW_RECORD_FAILED', `Failed to record resource view for ${viewData.resourceId}`);
    }
  }

  // Record resource download
  async recordResourceDownload(downloadData: Omit<ResourceDownloadData, 'id' | 'downloadedAt'>): Promise<ResourceDownloadData> {
    try {
      const existingDownload = await this.getUserResourceDownload(downloadData.userId, downloadData.resourceId);

      if (existingDownload) {
        // Update download count
        const updatedDownload = await this.updateResourceDownload(existingDownload.id, {
          downloadCount: existingDownload.downloadCount + 1
        });
        return updatedDownload;
      } else {
        // Create new download record
        const docRef = await addDoc(collection(db, this.downloadsCollectionName), {
          ...downloadData,
          downloadCount: 1,
          downloadedAt: serverTimestamp(),
        });

        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          throw new DRXError({
            code: 'RESOURCE_DOWNLOAD_CREATE_MISSING_DOCUMENT',
            devMessage: `Resource download for resource ${downloadData.resourceId} missing after create`,
          });
        }

        return {
          id: docRef.id,
          ...docSnap.data(),
        } as ResourceDownloadData;
      }
    } catch (error) {
      console.error('Error recording resource download:', error);
      throw this.toRepositoryError(error, 'RESOURCE_DOWNLOAD_RECORD_FAILED', `Failed to record resource download for ${downloadData.resourceId}`);
    }
  }

  // Get resource views
  async getResourceViews(resourceId: string, limitCount?: number): Promise<ResourceViewData[]> {
    try {
      let q = query(
        collection(db, this.viewsCollectionName),
        where('resourceId', '==', resourceId),
        orderBy('viewedAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const views: ResourceViewData[] = [];

      querySnapshot.forEach((doc) => {
        views.push({
          id: doc.id,
          ...doc.data(),
        } as ResourceViewData);
      });

      return views;
    } catch (error) {
      console.error('Error getting resource views:', error);
      throw this.toRepositoryError(error, 'RESOURCE_VIEWS_FETCH_FAILED', `Failed to get views for resource ${resourceId}`);
    }
  }

  // Get resource downloads
  async getResourceDownloads(resourceId: string): Promise<ResourceDownloadData[]> {
    try {
      const q = query(
        collection(db, this.downloadsCollectionName),
        where('resourceId', '==', resourceId),
        orderBy('downloadedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const downloads: ResourceDownloadData[] = [];

      querySnapshot.forEach((doc) => {
        downloads.push({
          id: doc.id,
          ...doc.data(),
        } as ResourceDownloadData);
      });

      return downloads;
    } catch (error) {
      console.error('Error getting resource downloads:', error);
      throw this.toRepositoryError(error, 'RESOURCE_DOWNLOADS_FETCH_FAILED', `Failed to get downloads for resource ${resourceId}`);
    }
  }

  // Get user resource views
  async getUserResourceViews(userId: string, limitCount?: number): Promise<ResourceViewData[]> {
    try {
      let q = query(
        collection(db, this.viewsCollectionName),
        where('userId', '==', userId),
        orderBy('viewedAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const views: ResourceViewData[] = [];

      querySnapshot.forEach((doc) => {
        views.push({
          id: doc.id,
          ...doc.data(),
        } as ResourceViewData);
      });

      return views;
    } catch (error) {
      console.error('Error getting user resource views:', error);
      throw this.toRepositoryError(error, 'RESOURCE_VIEWS_BY_USER_FETCH_FAILED', `Failed to get resource views for user ${userId}`);
    }
  }

  // Get user resource download
  async getUserResourceDownload(userId: string, resourceId: string): Promise<ResourceDownloadData | null> {
    try {
      const q = query(
        collection(db, this.downloadsCollectionName),
        where('userId', '==', userId),
        where('resourceId', '==', resourceId)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as ResourceDownloadData;
    } catch (error) {
      console.error('Error getting user resource download:', error);
      throw this.toRepositoryError(error, 'RESOURCE_DOWNLOAD_BY_USER_FETCH_FAILED', `Failed to get resource download for user ${userId} and resource ${resourceId}`);
    }
  }

  // Update resource download
  async updateResourceDownload(downloadId: string, updateData: Partial<ResourceDownloadData>): Promise<ResourceDownloadData> {
    try {
      const docRef = doc(db, this.downloadsCollectionName, downloadId);
      await updateDoc(docRef, updateData);

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'RESOURCE_DOWNLOAD_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Download record ${downloadId} not found after update`,
          userMessage: 'Download record not found.',
          level: 'warning',
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as ResourceDownloadData;
    } catch (error) {
      console.error('Error updating resource download:', error);
      throw this.toRepositoryError(error, 'RESOURCE_DOWNLOAD_UPDATE_FAILED', `Failed to update resource download ${downloadId}`);
    }
  }

  // Delete resource views
  async deleteResourceViews(resourceId: string): Promise<void> {
    try {
      const q = query(collection(db, this.viewsCollectionName), where('resourceId', '==', resourceId));
      const querySnapshot = await getDocs(q);

      const deletePromises = querySnapshot.docs.map(doc =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting resource views:', error);
      throw this.toRepositoryError(error, 'RESOURCE_VIEWS_DELETE_FAILED', `Failed to delete views for resource ${resourceId}`);
    }
  }

  // Delete resource downloads
  async deleteResourceDownloads(resourceId: string): Promise<void> {
    try {
      const q = query(collection(db, this.downloadsCollectionName), where('resourceId', '==', resourceId));
      const querySnapshot = await getDocs(q);

      const deletePromises = querySnapshot.docs.map(doc =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting resource downloads:', error);
      throw this.toRepositoryError(error, 'RESOURCE_DOWNLOADS_DELETE_FAILED', `Failed to delete downloads for resource ${resourceId}`);
    }
  }

  // Get resource statistics
  async getResourceStats(resourceId: string): Promise<{
    viewCount: number;
    downloadCount: number;
    uniqueViewers: number;
    totalTimeSpent: number;
  }> {
    try {
      const views = await this.getResourceViews(resourceId);
      const downloads = await this.getResourceDownloads(resourceId);

      const uniqueViewers = new Set(views.map(view => view.userId)).size;
      const totalTimeSpent = views.reduce((sum, view) => sum + (view.timeSpentSeconds || 0), 0);
      const totalDownloadCount = downloads.reduce((sum, download) => sum + download.downloadCount, 0);

      return {
        viewCount: views.length,
        downloadCount: totalDownloadCount,
        uniqueViewers,
        totalTimeSpent,
      };
    } catch (error) {
      console.error('Error getting resource stats:', error);
      throw this.toRepositoryError(error, 'RESOURCE_STATS_FETCH_FAILED', `Failed to get stats for resource ${resourceId}`);
    }
  }

  // ===== RESOURCE CATEGORY METHODS =====

  // Create resource category
  async createResourceCategory(categoryData: CreateResourceCategoryData): Promise<GetResourceCategoryData> {
    console.log('[ResourceRepository.createResourceCategory] START - CategoryData:', categoryData);
    try {
      const docRef = await addDoc(collection(db, this.categoriesCollectionName), {
        ...categoryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newCategory = await this.getResourceCategoryById(docRef.id);
      console.log('[ResourceRepository.createResourceCategory] SUCCESS - Created category:', docRef.id);
      return newCategory!;
    } catch (error) {
      console.error('[ResourceRepository.createResourceCategory] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCE_CATEGORY_CREATE_FAILED', 'Failed to create resource category');
    }
  }

  // Get resource category by ID
  async getResourceCategoryById(categoryId: string): Promise<GetResourceCategoryData | null> {
    console.log('[ResourceRepository.getResourceCategoryById] START - CategoryId:', categoryId);
    try {
      const docRef = doc(db, this.categoriesCollectionName, categoryId);
      const docSnapshot = await getDocs(query(collection(db, this.categoriesCollectionName), where('__name__', '==', categoryId)));
      
      if (docSnapshot.empty) {
        console.log('[ResourceRepository.getResourceCategoryById] Category not found');
        return null;
      }

      const categoryDoc = docSnapshot.docs[0];
      const categoryData = {
        id: categoryDoc.id,
        ...categoryDoc.data(),
      } as GetResourceCategoryData;

      console.log('[ResourceRepository.getResourceCategoryById] SUCCESS - Found category');
      return categoryData;
    } catch (error) {
      console.error('[ResourceRepository.getResourceCategoryById] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCE_CATEGORY_FETCH_FAILED', `Failed to get resource category ${categoryId}`);
    }
  }

  // Get all resource categories
  async getAllResourceCategories(limitCount?: number): Promise<GetResourceCategoryData[]> {
    console.log('[ResourceRepository.getAllResourceCategories] START - LimitCount:', limitCount);
    try {
      let q = collection(db, this.categoriesCollectionName);

      // Add limit if specified
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const categories: GetResourceCategoryData[] = [];

      querySnapshot.forEach((doc) => {
        categories.push({
          id: doc.id,
          ...doc.data(),
        } as GetResourceCategoryData);
      });

      console.log('[ResourceRepository.getAllResourceCategories] SUCCESS - Retrieved', categories.length, 'categories');
      return categories;
    } catch (error) {
      console.error('[ResourceRepository.getAllResourceCategories] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCE_CATEGORIES_FETCH_FAILED', 'Failed to get resource categories');
    }
  }

  // Update resource category
  async updateResourceCategory(categoryId: string, updateData: UpdateResourceCategoryData): Promise<GetResourceCategoryData> {
    console.log('[ResourceRepository.updateResourceCategory] START - CategoryId:', categoryId, 'UpdateData:', updateData);
    try {
      const docRef = doc(db, this.categoriesCollectionName, categoryId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const updatedCategory = await this.getResourceCategoryById(categoryId);
      console.log('[ResourceRepository.updateResourceCategory] SUCCESS - Updated category');
      return updatedCategory!;
    } catch (error) {
      console.error('[ResourceRepository.updateResourceCategory] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCE_CATEGORY_UPDATE_FAILED', `Failed to update resource category ${categoryId}`);
    }
  }

  // Delete resource category
  async deleteResourceCategory(categoryId: string): Promise<void> {
    console.log('[ResourceRepository.deleteResourceCategory] START - CategoryId:', categoryId);
    try {
      const docRef = doc(db, this.categoriesCollectionName, categoryId);
      await deleteDoc(docRef);
      console.log('[ResourceRepository.deleteResourceCategory] SUCCESS - Deleted category');
    } catch (error) {
      console.error('[ResourceRepository.deleteResourceCategory] ERROR:', error);
      throw this.toRepositoryError(error, 'RESOURCE_CATEGORY_DELETE_FAILED', `Failed to delete resource category ${categoryId}`);
    }
  }

  // ===== ADMIN / MODERATION METHODS (absorbed from firestore.ts) =====

  async getPendingResources(): Promise<GetResourceData[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => mapSnapToGetResourceData(d));
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_PENDING_FETCH_FAILED',
        devMessage: 'Failed to fetch pending resources',
        cause: error,
      });
    }
  }

  async getAllResourcesAdmin(): Promise<GetResourceData[]> {
    try {
      const snapshot = await getDocs(collection(db, this.collectionName));
      return snapshot.docs.map(d => mapSnapToGetResourceData(d));
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_ADMIN_FETCH_FAILED',
        devMessage: 'Failed to fetch all resources (admin)',
        cause: error,
      });
    }
  }

  async approveResource(resourceId: string, reason?: string): Promise<{ id: string; status: string }> {
    try {
      const docRef = doc(db, this.collectionName, resourceId);
      await updateDoc(docRef, {
        status: 'approved',
        isActive: true,
        approvedAt: serverTimestamp(),
        approvalReason: reason || 'Approved by admin',
        updatedAt: serverTimestamp(),
      });
      return { id: resourceId, status: 'approved' };
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_APPROVE_FAILED',
        devMessage: `Failed to approve resource ${resourceId}`,
        cause: error,
      });
    }
  }

  async rejectResource(resourceId: string, reason?: string): Promise<{ id: string; status: string }> {
    try {
      const docRef = doc(db, this.collectionName, resourceId);
      await updateDoc(docRef, {
        status: 'rejected',
        isActive: false,
        rejectedAt: serverTimestamp(),
        rejectionReason: reason || 'Rejected by admin',
        updatedAt: serverTimestamp(),
      });
      return { id: resourceId, status: 'rejected' };
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_REJECT_FAILED',
        devMessage: `Failed to reject resource ${resourceId}`,
        cause: error,
      });
    }
  }

  async toggleResourceFeatured(resourceId: string, isFeatured: boolean): Promise<{ id: string; isFeatured: boolean }> {
    try {
      const docRef = doc(db, this.collectionName, resourceId);
      await updateDoc(docRef, {
        isFeatured,
        updatedAt: serverTimestamp(),
      });
      return { id: resourceId, isFeatured };
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_TOGGLE_FEATURED_FAILED',
        devMessage: `Failed to toggle featured status for resource ${resourceId}`,
        cause: error,
      });
    }
  }

  async getResourcesBySeller(sellerId: string): Promise<GetResourceData[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where(RESOURCE_OWNER.sellerId, '==', sellerId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => mapSnapToGetResourceData(d));
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_SELLER_FETCH_FAILED',
        devMessage: `Failed to fetch resources for seller ${sellerId}`,
        cause: error,
      });
    }
  }
}
